import { useCallback, useState } from 'react';
import _ from 'lodash';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { Button } from '@material-ui/core';

import DataTable from '../../data-table/DataTable';
import { SELECTIONMODE_SINGLE } from '../../data-table/SelectionContext';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { buildPageId } from '../AdminLinkUtils';
import { AdminPages } from '../AdminPages';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { useGenericError } from '../../../services/FormErrorService';
import { useDataService } from '../../../services/ApiDataService';
import LimitDialog from './LimitDialog';

const functionName = AdminPages.ISNGs;

function getColumns() {
    return [
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'name',
            title: t`Name`,
            sortable: true,
            width: 305,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'status',
            title: t`Status`,
            sortable: true,
            width: 100,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`nPoints`,
            sortable: true,
            mapping: 'lastHourNPoints',
            width: 160,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`nPoints Limit`,
            sortable: true,
            mapping: 'limit',
            width: 160,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Uploads Last Hour`,
            sortable: true,
            mapping: 'lastHourTransfers',
            width: 170,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Uploads Failures`,
            sortable: true,
            mapping: 'lastHourFailures',
            width: 170,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'lastSeenAt',
            title: t`Last Upload`,
            sortable: true,
            width: 220,
            formatter: FORMATTER_TYPES.DATETIME,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'sysDescriptor',
            title: t`ISNG Version`,
            sortable: true,
            width: 420,
        },
    ];
}

const initialSort = [{ property: 'name', direction: SORT_ASC }];

const dataSource = {
    url: '/api/v3/isngs/list',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: ['name'],
    },
};

const setLimitMenuOption = {
    key: 'limit',
    text: i18n._(t`Limit`),
    action: 'set-limit',
};

const enableMenuOption = {
    key: 'enable',
    text: i18n._(t`Enable`),
    action: 'enable',
};

const disableMenuOption = {
    key: 'disable',
    text: i18n._(t`Disable`),
    action: 'disable',
};

const buttons = [setLimitMenuOption, enableMenuOption, disableMenuOption];

export default function ISNGList() {
    // Used by the data table to load data.
    const loader = useQueryLoader(dataSource, { disableFilterState: true });
    const handleGenericError = useGenericError();
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [tableSelection, setTableSelection] = useState(undefined);
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();
    const [limitDialogOpen, setLimitDialogOpen] = useState(false);
    const dataService = useDataService();

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setActionsDisabled(
            !currentTableSelection || (_.isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    const handleActionClick = useCallback(
        async ({ key, action }) => {
            try {
                if (key === 'limit') return setLimitDialogOpen(true);
                const [selectionId] = tableSelection.selection;
                await dataService.post(`/api/v3/isngs/${selectionId}/${action}`, {});
                refresher();
            } catch (e) {
                handleGenericError(e);
            }
        },
        [dataService, tableSelection, setLimitDialogOpen, refresher, handleGenericError]
    );

    const handleSetLimit = useCallback(
        async (limit) => {
            try {
                const [selectionId] = tableSelection.selection;
                await dataService.post(`/api/v3/isngs/${selectionId}/set-limit`, {}, { limit });
                setLimitDialogOpen(false);
                refresher();
            } catch (e) {
                handleGenericError(e);
            }
        },
        [dataService, tableSelection, refresher, handleGenericError]
    );

    return (
        <>
            <AdminPage
                title={t`ISNGs`}
                pageId={buildPageId(functionName)}
                actions={
                    <Actions>
                        {buttons.map((item) => (
                            <Button key={item.key} disabled={actionsDisabled} onClick={() => handleActionClick(item)}>
                                {item.text}
                            </Button>
                        ))}
                    </Actions>
                }
            >
                <DataTable
                    tableId="admin-isng-list"
                    loader={loader}
                    triggerRef={triggerRef}
                    columns={getColumns()}
                    visibleRows={0}
                    showSelectionColumn
                    sorters={initialSort}
                    selectionOptions={{
                        mode: SELECTIONMODE_SINGLE,
                        allowSelectAll: true,
                        idMapping: 'id',
                        onChange: handleTableSelectionChange,
                    }}
                />
            </AdminPage>
            <LimitDialog open={limitDialogOpen} setOpen={setLimitDialogOpen} onSave={handleSetLimit} />
        </>
    );
}
